/**
 * The "Adventures" tab: every run (session) the user has, grouped by adventure, with void, restore
 * and edit.
 *
 * Runs are VOIDED, never deleted. A voided run is hidden from the user everywhere — My Adventures,
 * leaderboards, ghosts — and stops earning credit, but the row, its track, journal and photos are
 * kept, and Restore puts it back (a completed run then earns its credit and steps again). "Remove
 * adventure from user" is simply voiding all of their runs of it.
 *
 * The list is one fetch of every run, newest first, voided included; grouping and filtering happen
 * here. Groups are collapsed accordions that unmount their table when closed, so a user with
 * hundreds of adventures does not render hundreds of tables.
 */

import { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Block, Edit, ExpandMore, Restore, Search } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { userDataApi } from '@api/endpoints/userData'
import type { AdminUserSessionDto } from '@api/types/userData'
import { EditRunDialog } from './UserDataEditDialogs'
import { UserDataActionDialog } from './UserDataActionDialog'
import { UserDataChangeResultDialog } from './UserDataChangeResultDialog'
import { adminUserSessionsKey } from './userQueryKeys'
import { errorMessage, formatDate, formatDistance, formatDuration } from './userFormatting'
import { useTablePaging } from './useTablePaging'
import { useUserDataChange } from './useUserDataChange'
import { useIsAdmin } from './useIsAdmin'

export interface UserAdventuresPanelProps {
  userId: string
}

interface AdventureGroup {
  adventureId: string
  title: string
  runs: AdminUserSessionDto[]
  voidedCount: number
}

/** Void or restore one run, or every run of the adventure in one state ("whole adventure"). */
interface RunsAction {
  kind: 'void' | 'restore'
  title: string
  runs: AdminUserSessionDto[]
  wholeAdventure: boolean
}

type RunAction = RunsAction | { kind: 'edit'; run: AdminUserSessionDto }

const GROUPS_PER_PAGE = 10

const isVoided = (run: AdminUserSessionDto) => Boolean(run.voidedAt)

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`

function statusColor(status: string): 'success' | 'info' | 'default' {
  if (status === 'Completed') return 'success'
  if (status === 'InProgress' || status === 'Active') return 'info'
  return 'default'
}

function voidMessage(action: RunsAction): string {
  const groupRun = action.runs.some((r) => r.participantCount > 1)
  const lead = action.wholeAdventure
    ? `Voids all ${plural(action.runs.length, 'run')} of it.`
    : `Voids this run from ${formatDate(action.runs[0]?.startedAt ?? action.runs[0]?.createdAt)}.`

  return (
    `${lead} Voided runs are hidden from the user everywhere and stop counting toward achievements, ` +
    'collections and steps — unless another completed run of the adventure remains. The runs, tracks and photos are ' +
    `kept, and can be restored.${groupRun ? ' This is a group run: it disappears for every participant too, though each keeps their own steps.' : ''}`
  )
}

function restoreMessage(action: RunsAction): string {
  return `Restores ${plural(action.runs.length, 'run')}. They are visible to the user again, and a completed run earns its credit and steps back.`
}

export function UserAdventuresPanel({ userId }: UserAdventuresPanelProps) {
  const change = useUserDataChange(userId)
  // Moderators see the runs read-only; void/restore/edit are Admin-only on the API too.
  const isAdmin = useIsAdmin()
  const [action, setAction] = useState<RunAction | null>(null)
  const [filter, setFilter] = useState('')
  const [groupPage, setGroupPage] = useState(1)

  const { data: sessions, isLoading, error } = useQuery({
    queryKey: adminUserSessionsKey(userId),
    queryFn: () => userDataApi.getSessions(userId),
  })

  // Grouped in the order the server sent (newest first), so the adventure run most recently sits on top.
  const groups = useMemo(() => {
    const byAdventure = new Map<string, AdventureGroup>()
    for (const run of sessions ?? []) {
      let group = byAdventure.get(run.adventureId)
      if (!group) {
        group = { adventureId: run.adventureId, title: run.adventureTitle || 'Untitled adventure', runs: [], voidedCount: 0 }
        byAdventure.set(run.adventureId, group)
      }
      group.runs.push(run)
      if (isVoided(run)) group.voidedCount++
    }
    return [...byAdventure.values()]
  }, [sessions])

  const term = filter.trim().toLowerCase()
  const filtered = term ? groups.filter((g) => g.title.toLowerCase().includes(term)) : groups
  const pageCount = Math.max(1, Math.ceil(filtered.length / GROUPS_PER_PAGE))
  const safeGroupPage = Math.min(groupPage, pageCount)
  const visibleGroups = filtered.slice((safeGroupPage - 1) * GROUPS_PER_PAGE, safeGroupPage * GROUPS_PER_PAGE)

  const totalRuns = sessions?.length ?? 0
  const totalVoided = groups.reduce((sum, g) => sum + g.voidedCount, 0)

  const close = () => {
    setAction(null)
    change.resetError()
  }

  const submitRuns = (kind: 'void' | 'restore', runs: AdminUserSessionDto[], reason: string) => {
    const request = { sessionIds: runs.map((r) => r.sessionId), reason }
    change.submit(
      () => (kind === 'void' ? userDataApi.voidSessions(userId, request) : userDataApi.restoreSessions(userId, request)),
      close,
    )
  }

  if (error) {
    return <Alert severity="error">{errorMessage(error, 'Failed to load this user’s runs')}</Alert>
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Removing runs voids them rather than deleting them: they disappear for the user and lose their credit, but are
        kept and can be restored. Award buttons are on the Overview tab.
      </Alert>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? 'Loading runs…'
            : `${plural(totalRuns, 'run')} across ${plural(groups.length, 'adventure')}${totalVoided ? ` · ${totalVoided.toLocaleString()} voided` : ''}`}
        </Typography>
        <TextField
          size="small"
          placeholder="Filter adventures"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value)
            setGroupPage(1)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 280 } }}
        />
      </Box>

      {!isLoading && filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {term ? 'No adventures match' : 'This user has no runs'}
        </Typography>
      )}

      <Stack spacing={1}>
        {visibleGroups.map((group) => (
          <AdventureRunGroup
            key={group.adventureId}
            group={group}
            canEdit={isAdmin}
            onVoid={(runs, wholeAdventure) => setAction({ kind: 'void', title: group.title, runs, wholeAdventure })}
            onRestore={(runs, wholeAdventure) =>
              setAction({ kind: 'restore', title: group.title, runs, wholeAdventure })
            }
            onEdit={(run) => setAction({ kind: 'edit', run })}
          />
        ))}
      </Stack>

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pageCount} page={safeGroupPage} onChange={(_, p) => setGroupPage(p)} size="small" />
        </Box>
      )}

      {action?.kind === 'edit' && (
        <EditRunDialog
          key={action.run.sessionId}
          row={action.run}
          pending={change.pending}
          error={change.error}
          onSubmit={(request) => change.submit(() => userDataApi.editSession(userId, action.run.sessionId, request), close)}
          onClose={close}
        />
      )}

      {action && action.kind !== 'edit' && (
        <UserDataActionDialog
          title={
            action.kind === 'void'
              ? action.wholeAdventure
                ? `Remove ${action.title} from this user?`
                : `Void this run of ${action.title}?`
              : `Restore ${action.runs.length === 1 ? 'this run' : 'these runs'} of ${action.title}?`
          }
          message={action.kind === 'void' ? voidMessage(action) : restoreMessage(action)}
          confirmLabel={action.kind === 'void' ? (action.wholeAdventure ? 'Remove adventure' : 'Void run') : 'Restore'}
          destructive={action.kind === 'void'}
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) => submitRuns(action.kind, action.runs, reason)}
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </Box>
  )
}

interface AdventureRunGroupProps {
  group: AdventureGroup
  /** False for a Moderator: the runs are shown without any void/restore/edit control. */
  canEdit: boolean
  onVoid: (runs: AdminUserSessionDto[], wholeAdventure: boolean) => void
  onRestore: (runs: AdminUserSessionDto[], wholeAdventure: boolean) => void
  onEdit: (run: AdminUserSessionDto) => void
}

function AdventureRunGroup({ group, canEdit, onVoid, onRestore, onEdit }: AdventureRunGroupProps) {
  const paging = useTablePaging()
  const activeRuns = group.runs.filter((r) => !isVoided(r))
  const voidedRuns = group.runs.filter(isVoided)

  const columns: DataTableColumn<AdminUserSessionDto>[] = [
    {
      key: 'started',
      header: 'Started',
      primary: true,
      render: (run) => (
        <>
          <Typography
            component="span"
            variant="body2"
            sx={{ display: 'block', color: isVoided(run) ? 'text.disabled' : undefined }}
          >
            {formatDate(run.startedAt ?? run.createdAt)}
          </Typography>
          {isVoided(run) && (
            <Typography component="span" variant="caption" color="error" sx={{ display: 'block' }}>
              Voided {formatDate(run.voidedAt ?? undefined)} by {run.voidedByName || 'unknown'}
              {run.voidReason ? ` — “${run.voidReason}”` : ''}
            </Typography>
          )}
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (run) => (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
          {isVoided(run) && <Chip size="small" color="error" label="Voided" />}
          <Chip size="small" variant="outlined" color={statusColor(run.status)} label={run.status} />
          {run.completionSource && run.completionSource !== 'None' && (
            <Chip size="small" variant="outlined" label={run.completionSource} />
          )}
          {run.isTestSession && <Chip size="small" color="warning" variant="outlined" label="Test" />}
        </Box>
      ),
    },
    { key: 'time', header: 'Time', render: (run) => formatDuration(run.durationSeconds) },
    { key: 'covered', header: 'Covered', hideBelow: 'md', render: (run) => formatDistance(run.coveredMeters) },
    { key: 'steps', header: 'Steps', align: 'right', render: (run) => (run.steps != null ? run.steps.toLocaleString() : '—') },
    { key: 'players', header: 'Players', align: 'right', hideBelow: 'lg', render: (run) => run.participantCount },
    { key: 'finished', header: 'Finished', hideBelow: 'lg', render: (run) => (run.completedAt ? formatDate(run.completedAt) : '—') },
  ]

  return (
    <Accordion disableGutters TransitionProps={{ unmountOnExit: true }} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {group.title}
          </Typography>
          <Chip size="small" variant="outlined" label={plural(group.runs.length, 'run')} />
          {group.voidedCount > 0 && <Chip size="small" color="error" label={`${group.voidedCount.toLocaleString()} voided`} />}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {canEdit && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Block />}
            disabled={activeRuns.length === 0}
            onClick={() => onVoid(activeRuns, true)}
          >
            Remove adventure from user
          </Button>
          {voidedRuns.length > 0 && (
            <Button variant="outlined" size="small" startIcon={<Restore />} onClick={() => onRestore(voidedRuns, true)}>
              Restore {voidedRuns.length === 1 ? 'voided run' : `all ${voidedRuns.length} voided runs`}
            </Button>
          )}
        </Stack>
        )}

        <DataTable
          rows={group.runs}
          columns={columns}
          getRowKey={(run) => run.sessionId}
          dense
          {...paging}
          getRowActions={(run) =>
            canEdit
              ? [
                  { key: 'edit', label: 'Edit run', icon: <Edit fontSize="small" />, hint: 'Edit time and distance', onClick: () => onEdit(run) },
                  isVoided(run)
                    ? { key: 'restore', label: 'Restore', icon: <Restore fontSize="small" />, onClick: () => onRestore([run], false) }
                    : { key: 'void', label: 'Void', icon: <Block fontSize="small" />, color: 'error' as const, onClick: () => onVoid([run], false) },
                ]
              : []
          }
        />
      </AccordionDetails>
    </Accordion>
  )
}
